import { ReactComponent as FloorIcon } from 'assets/icons/floor.svg';
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg';
import { ReactComponent as RoomIcon } from 'assets/icons/room.svg';
import { ReactComponent as VolumeIcon } from 'assets/icons/volume.svg';
import { ReactComponent as ArrowUpGreenIcon } from 'assets/icons/arrow-up-green.svg';
import { ReactComponent as PremiumIcon } from 'assets/icons/premium.svg';
import VipImg from 'assets/icons/vip.png';
import {
    cn,
    formatDate,
    formatPrice,
    getShowFloorInfo,
    getShowMaintenance,
    getShowMortgageStatus,
    getShowRoomCount,
} from 'lib/utils';
import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import AdvertBadge from '../advert-badge';
import { Skeleton } from '../skeleton';
import {
    DarxApplicationDtOsAdsDtOsAdsGetAllDto,
    useDeleteApiV1ClientAdsByIdMutation,
} from 'service/api/allApis';
import {
    ADVERT_TYPE,
    ADVERT_STATUS,
    MAINTENANCE_STATUS,
    MORTGAGE_STATUS,
    PRICE_TYPE,
    PROPERTY_TYPE,
    ADVERT_UPGRADE_PLAN,
} from 'types';
import { useTranslation } from 'react-i18next';
import { RenderIf } from '../render-if';
import { useLanguage } from 'hooks/use-language';
import { useSelector } from 'react-redux';
import { selectSettings, setFullPrice } from 'store/features/settingsSlice';
import { useAppDispatch } from 'store';
import ButtonOutline from '../button/outline';
import { Button } from '../button/default';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { paths } from 'lib/constants/paths';
import AdvertBoomark from '../advert-bookmark';
import { PopoverClose } from '@radix-ui/react-popover';
import { toast } from 'sonner';
import { useModal } from 'hooks/use-modal';

interface AdvertCardProps {
    advert: DarxApplicationDtOsAdsDtOsAdsGetAllDto;
    isCurrentUserOwner?: boolean;
}

const AdvertCard = ({ advert, isCurrentUserOwner = false }: AdvertCardProps) => {
    const { selectedLanguageId, selectedLanguageCode } = useLanguage();
    const [mutate, { isLoading, status }] = useDeleteApiV1ClientAdsByIdMutation({});
    const { isFullPrice, currency } = useSelector(selectSettings);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { onOpen } = useModal();

    const country = useMemo(
        () =>
            selectedLanguageId
                ? advert.country?.countryDictionaryGetAllDtos?.find(
                      (i) => i.languageId === selectedLanguageId,
                  )?.name
                : advert.country?.countryDictionaryGetAllDtos?.[0].name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );
    const city = useMemo(
        () =>
            selectedLanguageId
                ? advert.city?.cityDictionaryGetAllDtos?.find(
                      (i) => i.languageId === selectedLanguageId,
                  )?.name
                : advert.city?.cityDictionaryGetAllDtos?.[0].name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );

    const district = useMemo(
        () =>
            advert.district?.districtDictionaryGetAllDtos?.find(
                (i) => !selectedLanguageId || i.languageId === selectedLanguageId,
            )?.name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );
    const metro = useMemo(
        () =>
            advert.metro?.metroDictionaryGetAllDtos?.find(
                (i) => !selectedLanguageId || i.languageId === selectedLanguageId,
            )?.name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );
    const advertTypeTitle =
        advert.advertType === ADVERT_TYPE.RENT ? t('general.rent') : t('general.sale');
    const mortgageStatusTitle =
        advert.mortgageStatus === MORTGAGE_STATUS.YES
            ? t('advert.with-mortgage')
            : t('advert.without-mortgage');
    const priceType =
        advert.priceType === PRICE_TYPE.MONTHLY ? t('advert.monthly') : t('advert.daily');
    const maintenanceStatus =
        advert.maintenanceStatus === MAINTENANCE_STATUS.FULL
            ? t('advert.full-repair')
            : advert.maintenanceStatus === MAINTENANCE_STATUS.AVERAGE
            ? t('advert.average-repair')
            : t('advert.without-repair');
    const showMortgageStatus = getShowMortgageStatus(advert.advertType! as ADVERT_TYPE);
    const showRoomCount = getShowRoomCount(
        advert.propertyType! as PROPERTY_TYPE,
        advert.roomCount ?? 0,
    );
    const showFloorInfo = getShowFloorInfo(advert.propertyType! as PROPERTY_TYPE);
    const showMaintenance = getShowMaintenance(advert.propertyType! as PROPERTY_TYPE);
    const showPremiumBadge = !!(
        advert.isPremium && new Date(advert.premiumExpireDate!) > new Date()
    );
    const showVipBadge = !!(advert.isVIP && new Date(advert.vipExpireDate!) > new Date());

    let title;
    if (district) {
        title = district;
    }
    if (metro) {
        title += ', ' + metro;
    }
    if (!title) {
        title = city;
    }

    const address = `${country}, ${city}`;
    const price =
        isFullPrice || advert.advertType !== ADVERT_TYPE.SALE
            ? advert.price
            : Math.round(advert.price! / advert.propertyArea!) || 1;
    const imageData = useMemo(() => {
        if (!advert.images) return null;
        const images = [...advert.images];

        return images.sort((a, b) => a.index! - b.index!)?.[0] ?? null;
    }, [advert.images]);
    const isActive = advert.adsStatus === ADVERT_STATUS.Active;
    const isExpired = advert.adsStatus === ADVERT_STATUS.Expired;
    const isRejected = advert.adsStatus === ADVERT_STATUS.Rejected;
    const isPending = advert.adsStatus === ADVERT_STATUS.Pending;

    const advertStatusTitle = isActive
        ? t('advert.active')
        : isExpired
        ? t('advert.expired')
        : isRejected
        ? t('advert.rejected')
        : t('advert.pending');
    const advertStatusColor = isActive
        ? '#6AC259'
        : isExpired
        ? '#FF4F4F'
        : isRejected
        ? '#FF4F4F'
        : '#FAAD17';

    function handleDeleteAdvert() {
        mutate({ id: advert.id! })
            .unwrap()
            .then(() => {
                toast.success(t('success.advert-delete'));
            })
            .catch(() => {
                toast.error(t('error.advert-delete'));
            });
    }

    function handleReactivateADd() {
        onOpen('advertReactivate', {
            reactivateData: {
                advertId: advert.id!,
            },
        });
    }

    function openUpgradeModal(type: ADVERT_UPGRADE_PLAN) {
        onOpen('advertUpgrade', {
            upgradeData: {
                type,
                advertId: advert.id!,
            },
        });
    }

    useEffect(() => {
        if (status === 'fulfilled') {
            navigate(0);
        }
    }, [status]);

    return (
        <Link to={`/advert/${advert.id}`}>
            <div className="h-full min-h-[254px] md:min-h-[417px] py-1.5 sm:py-2.5 md:py-5 px-2 sm:px-3.5 border border-brend-20 rounded-[15px] bg-white cursor-pointer hover:shadow-advert-card duration-100">
                <div className="w-full h-[120px] md:h-[190px] relative mb-2 md:mb-5">
                    <img
                        src={imageData?.largeImage ?? ''}
                        alt="Advert"
                        className={cn(
                            'w-[calc(100%-4px)] h-full object-cover object-center ml-auto rounded-[15px]',
                        )}
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/not-found.png';
                        }}
                    />
                    <RenderIf condition={isCurrentUserOwner}>
                        <AdvertBadge
                            backgroundColor={advertStatusColor}
                            title={advertStatusTitle}
                            containerClassName="top-[100px] md:top-[160px] left-0"
                        />
                    </RenderIf>
                    <AdvertBadge
                        backgroundColor="#FFA920"
                        title={advertTypeTitle}
                        containerClassName="top-[20px] left-0"
                    />
                    <RenderIf condition={showMortgageStatus}>
                        <AdvertBadge
                            backgroundColor="#2087FF"
                            title={mortgageStatusTitle}
                            containerClassName="top-[34px] md:top-[45px] left-0"
                        />
                    </RenderIf>
                    <div className="absolute right-0.5 top-[93px]  md:top-[155px] items-center flex bg-white rounded-full px-0.5">
                        <RenderIf condition={showVipBadge}>
                            <img src={VipImg} className="w-6 md:w-8 object-contain " />
                        </RenderIf>
                        <RenderIf condition={showPremiumBadge}>
                            <PremiumIcon className="group-hover:!fill-white w-[32px] md:w-[36px] h-[22px] md:h-[28px] -mx-1" />
                        </RenderIf>
                    </div>
                    <AdvertBoomark advertId={advert.id!} />
                </div>
                <h3 className="text-txt text-sm md:text-[20px] font-bold line-clamp-1">{title}</h3>
                <p className="flex gap-x-2 items-center group w-fit cursor-pointer my-1.5 sm:my-[9px] md:mt-2 md:mb-4">
                    <LocationIcon className="fill-txt-40 w-[7px] h-[10px] md:w-[11.101px] md:h-[17px] group-hover:fill-txt/60 duration-100" />
                    <span className="text-[10px] md:text-base text-txt-40 font-semibold underline group-hover:text-txt/60 duration-100">
                        {address}
                    </span>
                </p>
                <div className="flex flex-wrap items-center  min-h-[28px]">
                    <span className="text-brend text-xs sm:text-[14px] md:text-[23px] tracking-tight font-semibold">
                        {formatPrice(price, +currency.exchangeRate)} {currency.code}
                    </span>
                    <RenderIf condition={advert.advertType === ADVERT_TYPE.RENT}>
                        <p className="lowercase text-txt-60 text-[8px] sm:text-[10px] md:text-[16px] font-bold translate-y-0.5">
                            /{priceType}
                        </p>
                    </RenderIf>
                    <RenderIf condition={advert.advertType === ADVERT_TYPE.SALE}>
                        <div className="flex text-[8px] md:text-sm ml-auto">
                            <span
                                className={cn(
                                    'flex items-center h-4 md:h-7  rounded-l-[15px] pl-1.5 sm:pl-3 md:pl-[15px] pr-1 sm:pr-2.5  font-medium',
                                    isFullPrice ? 'bg-brend text-white' : 'bg-dark-05 text-txt-40',
                                )}
                                onClick={(e) => {
                                    e.preventDefault();
                                    dispatch(setFullPrice(true));
                                }}
                            >
                                tam
                            </span>
                            <span
                                className={cn(
                                    'flex items-center h-4 md:h-7  rounded-r-[15px] pr-1.5 sm:pr-3 md:pr-[15px] pl-1 sm:pl-2.5  font-medium',
                                    isFullPrice ? 'bg-dark-05 text-txt-40' : 'bg-brend text-white',
                                )}
                                onClick={(e) => {
                                    e.preventDefault();
                                    dispatch(setFullPrice(false));
                                }}
                            >
                                1м2
                            </span>
                        </div>
                    </RenderIf>
                </div>
                <div className="flex justify-between items-center my-[9px] md:my-4 text-[10px] md:text-base">
                    <RenderIf condition={showRoomCount}>
                        <div className="flex items-center gap-x-1.5">
                            <RoomIcon className="md:w-fit md:h-fit w-[10px] h-[10px]" />
                            <span className="text-dark-60 font-medium">
                                {t('advert.room-with-count', { count: advert.roomCount! })}
                            </span>
                        </div>
                    </RenderIf>
                    <div className="flex items-center gap-x-1.5">
                        <VolumeIcon className="fill-dark-60 md:w-fit md:h-fit w-[10px] h-[10px]" />
                        <span className="text-dark-60 font-medium">
                            {advert.propertyArea}
                            {advert.propertyType === PROPERTY_TYPE.LAND ? 'sot' : 'm²'}
                        </span>
                    </div>
                    <RenderIf condition={showFloorInfo}>
                        <div className="flex items-center gap-x-1.5">
                            <FloorIcon className="fill-dark-60 md:w-fit md:h-fit w-[10px] h-[10px]" />
                            <span className="text-dark-60 font-medium">
                                {advert.propertyFloor}/{advert.buildingFloorCount}
                            </span>
                        </div>
                    </RenderIf>
                </div>
                <div className="flex justify-between text-second-60 font-medium text-[10px] md:text-sm">
                    <RenderIf condition={showMaintenance}>
                        <span className="whitespace-nowrap">{maintenanceStatus}</span>
                    </RenderIf>
                    <span className="ml-auto whitespace-nowrap">
                        {formatDate({ inputDate: advert.date!, locale: selectedLanguageCode! })}
                    </span>
                </div>
                <RenderIf condition={isCurrentUserOwner && isActive}>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                        className="grid md:grid-cols-3 gap-3 mt-2"
                    >
                        <Button
                            onClick={() => openUpgradeModal(ADVERT_UPGRADE_PLAN.FORWARD)}
                            variant={'outline'}
                            className="border-2 group border-[#6AC259] text-black hover:bg-[#6AC259]  flex justify-center items-center gap-x-3 md:text-lg py-6"
                        >
                            <ArrowUpGreenIcon className="group-hover:!fill-white w-[24px] h-[24px]" />
                        </Button>
                        <Button
                            onClick={() => openUpgradeModal(ADVERT_UPGRADE_PLAN.VIP)}
                            variant={'outline'}
                            className="border-2 group border-[#FAAD17] text-black hover:bg-[#ffdc41] flex justify-center items-center gap-x-3 md:text-lg py-6"
                        >
                            <img
                                src={VipImg}
                                className="w-[40px] md:w-full object-contain md:scale-75 "
                            />
                        </Button>
                        <Button
                            onClick={() => openUpgradeModal(ADVERT_UPGRADE_PLAN.PREMIUM)}
                            variant={'outline'}
                            className="border-2 group border-[#FAAD17] text-black hover:bg-[#FAAD17] flex justify-center items-center gap-x-3 md:text-lg py-6"
                        >
                            <PremiumIcon className="group-hover:!fill-white w-[28px] h-[28px]" />
                        </Button>
                    </div>
                </RenderIf>
                <RenderIf condition={isCurrentUserOwner && isExpired}>
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            handleReactivateADd();
                        }}
                        variant="outline"
                        className="w-full md:text-lg uppercase text-brend border-2 border-brend bg-brend-05 py-5 mt-2"
                    >
                        {t('advert.reactivate')}
                    </Button>
                </RenderIf>

                <RenderIf condition={isCurrentUserOwner && !isPending && !isExpired}>
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(paths.advert.update(String(advert.id)));
                        }}
                        variant="outline"
                        className="w-full md:text-lg  text-brend border-2 border-brend bg-brend-05 py-5 mt-2"
                    >
                        {t('advert.edit').toLocaleUpperCase('az-AZ')}
                    </Button>
                </RenderIf>
                <RenderIf condition={isCurrentUserOwner && isRejected}>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-full md:text-lg uppercase text-[#FB0000] border-2 border-[#FB0000] bg-brend-05 py-5 mt-2 hover:bg-[#FB0000]"
                                >
                                    {t('advert.reason-for-rejection')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                                <p>{advert.rejectNote}</p>
                            </PopoverContent>
                        </Popover>
                    </div>
                </RenderIf>
                <RenderIf condition={isCurrentUserOwner}>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-full md:text-lg uppercase text-[#FB0000] border-2 border-[#FB0000] bg-brend-05 py-5 mt-2 hover:bg-[#FB0000]"
                                >
                                    {t('advert.delete')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                                <p className="font-bold text-muted-foreground">
                                    {t('advert.delete-confirm')}
                                </p>
                                <div className="flex gap-x-2 pt-2">
                                    <PopoverClose asChild>
                                        <ButtonOutline text={t('general.no')} className="w-full" />
                                    </PopoverClose>
                                    <ButtonOutline
                                        disabled={isLoading}
                                        loading={isLoading}
                                        onClick={handleDeleteAdvert}
                                        text={t('general.yes')}
                                        className="w-full"
                                    />
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </RenderIf>
            </div>
        </Link>
    );
};

AdvertCard.Skeleton = function () {
    return (
        <div className="md:min-w-[280px] min-h-[254px] md:min-h-[417px] py-1.5 sm:py-2.5 md:py-5 px-2 sm:px-3.5 border border-brend-20 rounded-[15px] bg-white cursor-pointer hover:shadow-advert-card duration-100">
            <div className="w-full h-[120px] md:h-[190px] relative mb-2 md:mb-5">
                <Skeleton className="w-full h-full rounded-[15px]" />
            </div>
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2 mb-6" />
            <div className="flex flex-wrap items-center justify-between mb-6">
                <Skeleton className="h-6 w-3/5" />
                <Skeleton className="h-6 w-1/3" />
            </div>
            <div className="flex justify-between items-center mb-4">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/4" />
            </div>
            <div className="flex justify-between">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-1/4" />
            </div>
        </div>
    );
};

export default AdvertCard;
